import React from 'react';
import Icon from '../../../components/AppIcon';

const UniversityPathway = ({ formData, errors, onChange, onCheckboxChange }) => {
  const baccalaureateYearOptions = Array.from({ length: 10 }, (_, i) => {
    const year = new Date()?.getFullYear() - i;
    return { value: year?.toString(), label: year?.toString() };
  });

  const seriesOptions = [
    { value: 'l', label: 'Série L (Littéraire)' },
    { value: 's', label: 'Série S (Scientifique)' },
    { value: 'es', label: 'Série ES (Économique et Social)' },
    { value: 'g', label: 'Série G (Gestion)' },
    { value: 't', label: 'Série T (Technique)' }
  ];

  const fieldOptions = [
    { id: 'sciences', label: 'Sciences et Technologies', icon: 'Atom' },
    { id: 'engineering', label: 'Ingénierie', icon: 'Cpu' },
    { id: 'medicine', label: 'Médecine et Santé', icon: 'Heart' },
    { id: 'business', label: 'Commerce et Gestion', icon: 'TrendingUp' },
    { id: 'law', label: 'Droit et Sciences Politiques', icon: 'Scale' },
    { id: 'humanities', label: 'Sciences Humaines', icon: 'BookOpen' },
    { id: 'arts', label: 'Arts et Design', icon: 'Palette' },
    { id: 'education', label: 'Éducation', icon: 'GraduationCap' }
  ];

  const universityPreferenceOptions = [
    { id: 'ucad', label: 'Université Cheikh Anta Diop (UCAD)' },
    { id: 'uam', label: 'Université Amadou Mahtar Mbow (UAM)' },
    { id: 'ugb', label: 'Université Gaston Berger (UGB)' },
    { id: 'ussein', label: 'Université Sine Saloum El Hadji Ibrahima Niass' },
    { id: 'uasz', label: 'Université Assane Seck de Ziguinchor (UASZ)' },
    { id: 'ut', label: 'Université de Thiès (UT)' },
    { id: 'private', label: 'Universités Privées' },
    { id: 'abroad', label: 'Études à l\'Étranger' }
  ];

  return (
    <div className="space-y-6">
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center">
            <Icon name="Award" size={24} color="#FFFFFF" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Parcours Universitaire
            </h3>
            <p className="text-sm text-gray-600">
              Complète ton profil académique pour des recommandations précises
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Année d'Obtention du Baccalauréat <span className="text-red-500">*</span>
          </label>
          <select
            name="baccalaureateYear"
            value={formData?.baccalaureateYear}
            onChange={onChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="">Sélectionnez l'année</option>
            {baccalaureateYearOptions?.map((year) => (
              <option key={year?.value} value={year?.value}>
                {year?.label}
              </option>
            ))}
          </select>
          {errors?.baccalaureateYear && (
            <p className="text-sm text-red-500 mt-1">{errors?.baccalaureateYear}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Série du Baccalauréat
          </label>
          <select
            name="baccalaureateSeries"
            value={formData?.baccalaureateSeries}
            onChange={onChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="">Sélectionnez votre série</option>
            {seriesOptions?.map((series) => (
              <option key={series?.value} value={series?.value}>
                {series?.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-base font-semibold text-gray-900 mb-4">
          Domaine d'Études Souhaité <span className="text-red-500">*</span>
        </label>
        <p className="text-sm text-gray-600 mb-4">
          Quel domaine souhaitez-vous approfondir dans vos études supérieures ?
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {fieldOptions?.map((field) => (
            <label
              key={field?.id}
              className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                formData?.desiredField === field?.id
                  ? 'border-purple-500 bg-purple-50' :'border-gray-200 hover:border-purple-300'
              }`}
            >
              <input
                type="radio"
                name="desiredField"
                value={field?.id}
                checked={formData?.desiredField === field?.id}
                onChange={onChange}
                className="w-5 h-5 text-purple-500 focus:ring-purple-500"
              />
              <Icon name={field?.icon} size={24} className="text-purple-600" />
              <span className="text-sm font-medium text-gray-900">{field?.label}</span>
            </label>
          ))}
        </div>
        {errors?.desiredField && (
          <p className="text-sm text-red-500 mt-2">{errors?.desiredField}</p>
        )}
      </div>

      <div>
        <label className="block text-base font-semibold text-gray-900 mb-4">
          Préférences Universitaires
        </label>
        <p className="text-sm text-gray-600 mb-4">
          Sélectionnez les universités qui vous intéressent
        </p>
        <div className="space-y-3">
          {universityPreferenceOptions?.map((university) => (
            <label
              key={university?.id}
              className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                formData?.universityPreferences?.includes(university?.id)
                  ? 'border-purple-500 bg-purple-50' :'border-gray-200 hover:border-purple-300'
              }`}
            >
              <input
                type="checkbox"
                checked={formData?.universityPreferences?.includes(university?.id)}
                onChange={(e) => onCheckboxChange('universityPreferences', university?.id, e?.target?.checked)}
                className="w-5 h-5 text-purple-500 rounded focus:ring-purple-500"
              />
              <span className="text-sm font-medium text-gray-900">{university?.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Formation Antérieure (Optionnel)
        </label>
        <textarea
          name="previousEducation"
          value={formData?.previousEducation}
          onChange={onChange}
          rows={4}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          placeholder="Décrivez vos études antérieures, diplômes obtenus, ou expériences académiques pertinentes..."
        />
      </div>
    </div>
  );
};

export default UniversityPathway;